import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty } from "lodash";

import SimulationInfo, { Section, TableHeader, TableRow } from "./SimulationInfo";
import FuelPrices from "./FuelPrices";

import { getLabel } from "utils/dcfExport/details";
import { getSortableName } from "utils/directory/sorting";
import { checkIsSimulationRunning } from "utils/communities";
import { startCommunityParametricRun, startCommunityEcmValidation } from "utils/communities/api";
import { getCommunityMeasureValidationStats } from "utils/communities/communityValidation";
import { useStateSelector } from "utils/field";
import { canRunPrecheck } from "utils/parametric";

import { getMissingEcmInputMapCodes, getTableUpgradesMissingFromEcmInputMap } from "utils/upgrades/ecmInputMap";
import {
    getCommunityValidationComboCount,
    COMMUNITY_VALIDATION_COMBO_MIN,
    COMMUNITY_VALIDATION_COMBO_MAX,
} from "utils/communities/validationComboCount";

import { fetchCommunityRunHistory, saveCommunity, saveEcmInputMap } from "store/community/thunk";
import { saveUpgrades } from "../Upgrades/_ducks/thunk";
import { selectUser } from "store/users/selectors";
import {
    selectCommunityEcmInputMap,
    selectCommunityModelDir,
    selectCommunityRunHistory,
    selectModelUpgradesRun,
} from "store/community/selectors";
import { selectUpgradePackagesSortedArray } from "../Upgrades/_ducks/selectors";
import commParametricRunConfig from "../templates/commParametricRunConfig.json";
import { useCommunityValidationLock } from "../CommunityValidationLockContext";

import classes from "./style.module.scss";
import sharedClasses from "../style.module.scss";
import Upgrades from "assets/images/icons/JSX/Upgrades";
import House from "assets/images/icons/JSX/House";
import Ready from "assets/images/icons/Ready.svg";
import Error from "assets/images/icons/warning.svg";
import Unknown from "assets/images/icons/warning-yellow.svg";

//"../templates/parametricRunConfig-testSmall.json";

const Configuration = ({
    history,
    communityId,
    uid,
    user,
    modelDir: { models = {} },
    ecmInputMap = {},
    upgradePackages = {},
    runHistory = {},
    modelUpgradesRun = {}, //have to pass this in the front-end because there are two separate calls, and the second call needs the initial model upgrades run
    fetchCommunityRunHistory,
    saveCommunityUpgrades,
    saveCommunityData,
    saveEcmInputMap,
}) => {
    const { preferredUnits = "Metric" } = useStateSelector("user.userPreferences") || {};

    const {
        validationUiLocked,
        validationTimedOut,
        serverSaysValidationPending,
        lastValidationRun = {},
    } = useCommunityValidationLock();

    const [isPreparingRun, toggleIsPreparingRun] = useState(false);
    const [runInfo, setRunInfo] = useState({});
    const [selectedValidationModelId, setSelectedValidationModelId] = useState("");
    const [disableValidateClick, setDisableValidateClick] = useState(false);
    const [validationStarting, setValidationStarting] = useState(false);

    const measureStats = useMemo(
        () => getCommunityMeasureValidationStats(ecmInputMap, upgradePackages),
        [ecmInputMap, upgradePackages]
    );

    const upgradePackagesMap = useMemo(
        () => upgradePackages.reduce((acc, pkg) => ({ ...acc, [pkg.id]: pkg }), {}),
        [upgradePackages]
    );

    const ecmTableSyncIssues = useMemo(
        () => getTableUpgradesMissingFromEcmInputMap(upgradePackagesMap, ecmInputMap),
        [upgradePackagesMap, ecmInputMap]
    );

    const validationComboCount = useMemo(() => getCommunityValidationComboCount(ecmInputMap), [ecmInputMap]);

    const validationComboOutOfRange =
        validationComboCount < COMMUNITY_VALIDATION_COMBO_MIN || validationComboCount > COMMUNITY_VALIDATION_COMBO_MAX;

    /** Server reports an in-flight validation and the client timeout has not fired yet — locks controls and Models/Upgrades tabs. */
    const validationInProgress = validationUiLocked;

    useEffect(() => {
        if (serverSaysValidationPending) {
            setValidationStarting(false);
        }
    }, [serverSaysValidationPending]);

    const validationRefModelName =
        lastValidationRun?.modelId && models[lastValidationRun.modelId]?.name
            ? models[lastValidationRun.modelId].name
            : "";

    let validationModelCaption = "";
    if (validationStarting && selectedValidationModelId) {
        const startingModelName = models[selectedValidationModelId]?.name;
        validationModelCaption = startingModelName
            ? `Starting validation (${startingModelName})`
            : "Starting validation";
    } else if (validationRefModelName && lastValidationRun?.id) {
        if (validationInProgress) {
            validationModelCaption = `Validation in progress using: ${validationRefModelName}`;
        } else if (lastValidationRun.complete) {
            validationModelCaption = `Last validation run used: ${validationRefModelName}`;
        } else if (validationTimedOut && serverSaysValidationPending) {
            validationModelCaption = `Validation did not complete within 2 minutes for: ${validationRefModelName}. You can try again.`;
        }
    }

    const readyModelOptions = useMemo(
        () =>
            Object.entries(models)
                .filter(([, m]) => m.status === "ready")
                .map(([id, m]) => ({ id, name: m.name }))
                .sort((a, b) => getSortableName(a?.name).localeCompare(getSortableName(b?.name))),
        [models]
    );

    useEffect(() => {
        const ids = readyModelOptions.map((o) => o.id);
        if (ids.length === 0) {
            setSelectedValidationModelId("");
            return;
        }
        setSelectedValidationModelId((prev) => (prev && ids.includes(prev) ? prev : ids[0]));
    }, [readyModelOptions]);

    const validationModelDiffersFromLastRun =
        Boolean(selectedValidationModelId) &&
        Boolean(lastValidationRun?.modelId) &&
        selectedValidationModelId !== lastValidationRun.modelId;

    const disableValidateButton =
        disableValidateClick ||
        !selectedValidationModelId ||
        readyModelOptions.length === 0 ||
        Object.values(ecmInputMap).every((upgradeToCheck) => Object.keys(upgradeToCheck).length === 1) ||
        (!canRunPrecheck(ecmInputMap) && !validationModelDiffersFromLastRun) ||
        validationUiLocked ||
        validationStarting ||
        validationComboOutOfRange ||
        ecmTableSyncIssues.length > 0;

    const communityRunBlocked =
        validationUiLocked ||
        validationStarting ||
        !measureStats.measuresAllPassing ||
        measureStats.totalNonNone === 0 ||
        ecmTableSyncIssues.length > 0;

    const numUpgradePackages = upgradePackages.length;
    let uniqueModels = 0;
    let numBaseFiles = 0;

    // Sort models by status and name
    const sortedModels = Object.entries(models).sort(([aKey, aValues], [bKey, bValues]) => {
        if (aValues.status === "ready" && bValues.status !== "ready") return -1;
        if (aValues.status !== "ready" && bValues.status === "ready") return 1;
        return getSortableName(aValues?.name).localeCompare(getSortableName(bValues?.name));
    });

    // Filter out unready models
    const readyModels = sortedModels.filter(([id, modelObj]) => modelObj.status === "ready");

    // Count the number of unique models and base files (includes duplicates)
    readyModels.forEach(([id, modelObj]) => {
        const { countDirection } = modelObj;
        uniqueModels += Object.values(countDirection.copyDirections).filter((val) => val > 0).length;
        numBaseFiles += countDirection.duplicates;
    });

    useEffect(async () => {
        const runHistory = await fetchCommunityRunHistory(communityId);

        const readyModelIds = readyModels.map(([id, model]) => id);
        var numPassedResults = 0;
        var numFailedResults = 0;
        var failedModelInfo = {};

        // Count number of passed and failed results for ready models
        Object.values(runHistory.runResults).forEach(({ results = {} }) => {
            Object.entries(results).forEach(([key, value]) => {
                var modelId = key.split("_")[0];

                if (!readyModelIds.includes(modelId)) return;

                switch (value.result) {
                    case "PASS":
                        numPassedResults++;
                        break;
                    case "FAIL":
                        numFailedResults++;

                        const modelName = readyModels.find(([id, model]) => id === modelId)?.[1]?.name;
                        const failedPackageName = upgradePackages.reduce(
                            (acc, upgrade) => (upgrade.id === value.upgradePackageId ? upgrade.name : acc),
                            "Base Case"
                        );

                        failedModelInfo[modelId] = {
                            modelName,
                            upgradePackages: [...(failedModelInfo[modelId]?.upgradePackages || []), failedPackageName],
                        };
                        break;
                }
            });
        });

        const numCompletedRuns = numPassedResults + numFailedResults;
        const numTotalSimulations = numUpgradePackages > 0 ? uniqueModels * (numUpgradePackages + 1) : 0;
        const sortedFailedModelInfo = Object.entries(failedModelInfo).sort(([keyA, valueA], [keyB, valueB]) =>
            getSortableName(valueA?.modelName).localeCompare(getSortableName(valueB?.modelName))
        );

        const runInfo = {
            numFailed: numFailedResults,
            numCompleted: numCompletedRuns,
            numToBeRun: numTotalSimulations - numCompletedRuns,
            totalSimulations: numTotalSimulations,
            numCombinations: (1 + numUpgradePackages) ** numBaseFiles,
            failedModelInfo: Object.fromEntries(sortedFailedModelInfo),
        };

        setRunInfo(runInfo);
    }, [communityId]);

    const getDirectionLabel = (shortHand) => {
        const index = ["S", "SE", "E", "NE", "N", "NW", "W", "SW"].indexOf(shortHand);
        return getLabel("facingDirection", index);
    };

    /** Persist community + upgrades + ECM map to Firestore; returns normalized ecmInputMap from save. */
    const persistCommunityForSimulation = async () => {
        await saveCommunityData(uid);
        await saveCommunityUpgrades();
        const savedMap = await saveEcmInputMap(communityId);
        return savedMap;
    };

    const handleCommunityValidateClick = async () => {
        setValidationStarting(true);
        setDisableValidateClick(true);
        try {
            const savedMap = await persistCommunityForSimulation();
            if (!communityId || !selectedValidationModelId) {
                setValidationStarting(false);
                return;
            }

            const { uid: userUid = "", email: userEmail = "" } = user || {};
            const mapToSend = savedMap || ecmInputMap;

            const validationResult = await startCommunityEcmValidation({
                communityId,
                modelId: selectedValidationModelId,
                parametricRunConfig: {
                    ...commParametricRunConfig,
                    user: {
                        uid: userUid || uid,
                        email: userEmail,
                    },
                    ecmInputMap: mapToSend,
                },
            });
            if (!validationResult.ok) {
                setValidationStarting(false);
                window.alert(
                    validationResult.message ||
                        `Validation could not be started${validationResult.status ? ` (HTTP ${validationResult.status})` : ""}.`
                );
            }
        } catch {
            setValidationStarting(false);
        } finally {
            setDisableValidateClick(false);
        }
    };

    const handleCommunityParametricRunClick = async () => {
        toggleIsPreparingRun(true);
        try {
            const savedMap = await persistCommunityForSimulation();
            const mapToSend = savedMap || ecmInputMap;

            if (communityId && numUpgradePackages >= 1) {
                const packagesMap = upgradePackages.reduce(
                    (acc, curr) => ({
                        ...acc,
                        [curr.id]: curr,
                    }),
                    {}
                );

                const resultsWholePackages = await startCommunityParametricRun({
                    communityId,
                    models,
                    upgradePackages: packagesMap,
                    parametricRunConfig: {
                        ...commParametricRunConfig,
                        user: {
                            uid,
                        },
                        ecmInputMap: mapToSend,
                        buildType: "commWholePackages",
                    },
                    initModelUpgradesRun: modelUpgradesRun,
                });

                if (!resultsWholePackages.ok) {
                    window.alert(
                        resultsWholePackages.message ||
                            `Community run could not start${resultsWholePackages.status ? ` (HTTP ${resultsWholePackages.status})` : ""}.`
                    );
                    return;
                }

                const resultsByMeasure = await startCommunityParametricRun({
                    communityId,
                    models,
                    upgradePackages: packagesMap,
                    parametricRunConfig: {
                        ...commParametricRunConfig,
                        user: {
                            uid,
                        },
                        ecmInputMap: mapToSend,
                        buildType: "commByMeasure",
                    },
                    initModelUpgradesRun: modelUpgradesRun,
                });

                if (!resultsByMeasure.ok) {
                    window.alert(
                        resultsByMeasure.message ||
                            `Community run could not start${resultsByMeasure.status ? ` (HTTP ${resultsByMeasure.status})` : ""}.`
                    );
                    return;
                }

                if (resultsWholePackages.status === 202 && resultsByMeasure.status === 202) {
                    history.push(`/communities/${communityId}/results`);
                }
            }
        } finally {
            toggleIsPreparingRun(false);
        }
    };

    const isSimulationRunning = useMemo(() => checkIsSimulationRunning(runHistory), [runHistory]);

    const missingCodes = useMemo(
        () => getMissingEcmInputMapCodes(upgradePackages, ecmInputMap),
        [upgradePackages, ecmInputMap]
    );

    return (
        <div className={sharedClasses.contentContainer}>
            <SimulationInfo
                runInfo={runInfo}
                handleCommunityParametricRunClick={handleCommunityParametricRunClick}
                isPreparingRun={isPreparingRun}
                isSimulationRunning={isSimulationRunning}
                hasMissingCodes={!isEmpty(missingCodes)}
                ecmTableSyncIssues={ecmTableSyncIssues}
                validationComboCount={validationComboCount}
                validationComboMax={COMMUNITY_VALIDATION_COMBO_MAX}
                validationComboOutOfRange={validationComboOutOfRange}
                communityRunBlocked={communityRunBlocked}
                readyModelOptions={readyModelOptions}
                selectedValidationModelId={selectedValidationModelId}
                onValidationModelChange={setSelectedValidationModelId}
                validationModelCaption={validationModelCaption}
                measureStats={measureStats}
                disableValidate={disableValidateButton}
                validationInProgress={validationInProgress || validationStarting}
                validationControlsLocked={validationUiLocked || validationStarting}
                onValidateClick={handleCommunityValidateClick}
            />

            {/* <Section
                title={`${uniqueModels} Unique Models (${numBaseFiles} Total Models)`}
                subtitle={`${Object.keys(models).length - uniqueModels} models were omitted`
            }> */}
            <Section title={`${uniqueModels} Unique Models (${numBaseFiles} Total Models)`}>
                <TableHeader gridTemplateColumns="1fr 1fr 0.25fr">
                    <p>Base Model Name</p>
                    <p>Duplicates</p>
                    <p className={classes.status}>Status</p>
                </TableHeader>

                {sortedModels.map(([id, model]) => {
                    const copyDirections = Object.entries(model.countDirection.copyDirections).filter(
                        ([key, value]) => value > 0
                    );
                    return (
                        <TableRow
                            id={`base-model-${id}`}
                            key={`base-model-${id}`}
                            disabled={model.status !== "ready"}
                            gridTemplateColumns="1fr 1fr 0.25fr"
                        >
                            <div className={classes.itemTitle}>
                                <House color={"#f58020"} /> <p>{model.name}</p>
                            </div>
                            <p className={classes.countDirection}>
                                {copyDirections.map(([key, value], index) => (
                                    <span id={`${id}-${key}`} key={`${id}-${key}`}>
                                        {value} {getDirectionLabel(key)}
                                        {index < copyDirections.length - 1 ? "," : ""}
                                        {index === 3 ? <br /> : " "}
                                    </span>
                                ))}
                            </p>
                            <div className={classes.status}>
                                <img
                                    src={
                                        model.status === "ready"
                                            ? Ready
                                            : model.status === "error"
                                              ? Error
                                              : model.status === "unknown"
                                                ? Unknown
                                                : ""
                                    }
                                />
                            </div>
                        </TableRow>
                    );
                })}
            </Section>

            <Section title={`${numUpgradePackages} Upgrade Packages (${numUpgradePackages + 1} Total Packages)`}>
                <TableHeader gridTemplateColumns="1fr">
                    <p>Upgrade Package Name</p>
                </TableHeader>
                <TableRow gridTemplateColumns="1fr">
                    <p className={classes.itemTitle}>
                        <House color={"#92a4ad"} /> Base Case
                    </p>
                </TableRow>
                {upgradePackages.map((upgrade) => (
                    <TableRow key={`package-${upgrade.name}`} gridTemplateColumns="1fr">
                        <p className={classes.itemTitle}>
                            <Upgrades color={"#f58020"} /> {upgrade.name}
                        </p>
                    </TableRow>
                ))}
            </Section>
            <FuelPrices modelUnits={preferredUnits} />
        </div>
    );
};

const mapStateToProps = createStructuredSelector({
    user: selectUser,
    modelDir: selectCommunityModelDir,
    ecmInputMap: selectCommunityEcmInputMap,
    upgradePackages: selectUpgradePackagesSortedArray,
    runHistory: selectCommunityRunHistory,
    modelUpgradesRun: selectModelUpgradesRun,
});

const mapDispatchToProps = (dispatch) => ({
    fetchCommunityRunHistory: async (communityId) => {
        return dispatch(fetchCommunityRunHistory(communityId));
    },
    saveCommunityUpgrades: () => dispatch(saveUpgrades()),
    saveCommunityData: (uid) => dispatch(saveCommunity(uid)),
    saveEcmInputMap: (id) => dispatch(saveEcmInputMap(id)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Configuration);
